import { useMutation, useQuery, useQueryClient } from "react-query";
import { getChatThreadMessages, getChatThreads, getChatThreadsUnreadMessagesCount, getChatThreadUnreadMessagesCount, getUnreadThreadsCount, postBulkThreadStateAction, postChatThreadMessage, postThreadStateAction } from "./requests";
import { notifyError } from "@/toast-notifications";
import { useTranslation } from "react-i18next";
import { doesActionAffectUnreadThreadsCount } from "../../utils/threads";
import useReduxStore from "@/hooks/useReduxStore";
import { THREAD_STATE_ACTIONS } from "../../constants";

export const CHAT_THREADS_KEY = "chat-threads";

export function useGetChatThreads(params = {}, options = {}) {
    const { slice: messagingEnabled } = useReduxStore(state => state.settingsReducer.restaurantSettings.messaging_enabled);
    const { t } = useTranslation();

    return useQuery(
        [CHAT_THREADS_KEY, "list", params],
        () => getChatThreads(params),
        {
            ...options,
            enabled: Boolean(messagingEnabled),
            onError: () => {
                notifyError(t("queries:threads.error.get_chat_threads"));
            }
        }
    );
}

export const CHAT_THREAD_MESSAGES_KEY = "chat-thread-messages";

export function useGetChatThreadMessages(threadId, params = {}, options = {}) {
    const { t } = useTranslation();
    const { slice: messagingEnabled } = useReduxStore(state => state.settingsReducer.restaurantSettings.messaging_enabled);

    return useQuery(
        [CHAT_THREADS_KEY, threadId, CHAT_THREAD_MESSAGES_KEY, params],
        () => getChatThreadMessages(threadId, params),
        {
            ...options,
            enabled: Boolean(messagingEnabled) && !!threadId,
            onError: () => {
                notifyError(t("queries:threads.error.get_chat_thread_messages"));
            }
        }
    );
}

export function usePostChatThreadMessage() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation(
        ({ threadId, content }) => postChatThreadMessage(threadId, content),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([CHAT_THREADS_KEY]);
            },
            onError: () => {
                notifyError(t("queries:threads.error.post_chat_thread_message"));
            }
        }
    );
}


export function usePostThreadStateAction() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation(
        ({ threadId, action }) => postThreadStateAction(threadId, action),
        {
            onSuccess: (_, variables) => {
                const { threadId, action } = variables;

                if (action === THREAD_STATE_ACTIONS.READ) {
                    setCachedUnreadMessagesCount(queryClient, threadId, {
                        id: threadId,
                        unread_messages_count: 0,
                    });
                }

                if (doesActionAffectUnreadThreadsCount(action)) {
                    queryClient.invalidateQueries([CHAT_THREADS_KEY]);
                    return;
                }

                queryClient.invalidateQueries([CHAT_THREADS_KEY, threadId]);
                queryClient.invalidateQueries([CHAT_THREADS_KEY, "list"]);
            },
            onError: () => {
                notifyError(t("queries:threads.error.post_chat_thread_state_action"));
            }
        }
    );
}

export function usePostBulkThreadStateAction() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation(
        ({ threadsIds, action }) => postBulkThreadStateAction(threadsIds, action),
        {
            onSuccess: (_, variables) => {
                const { threadsIds, action } = variables;

                if (action === THREAD_STATE_ACTIONS.READ) {
                    setChatThreadsUnreadMessagesCountToZero(queryClient, threadsIds);
                }

                queryClient.invalidateQueries([CHAT_THREADS_KEY]);
            },
            onError: () => {
                notifyError(t("queries:threads.error.post_bulk_chat_thread_state_action"));
            }
        }
    );
}

export const CHAT_THREAD_UNREAD_MESSAGES_COUNT_KEY = "chat-thread-unread-messages-count";

export function useGetChatThreadUnreadMessagesCount(threadId, options = {}) {
    const { t } = useTranslation();
    const { slice: messagingEnabled } = useReduxStore(state => state.settingsReducer.restaurantSettings.messaging_enabled);

    return useQuery(
        [CHAT_THREADS_KEY, threadId, CHAT_THREAD_UNREAD_MESSAGES_COUNT_KEY],
        () => getChatThreadUnreadMessagesCount(threadId),
        {
            ...options,
            enabled: Boolean(messagingEnabled) && !!threadId,
            onError: () => {
                notifyError(t("queries:threads.error.get_chat_thread_unread_messages_count"));
            },
        }
    );
}

export const UNREAD_THREADS_COUNT_KEY = "unread-threads-count";
export function useGetUnreadThreadsCount(options = {}) {
    const { t } = useTranslation();
    const { slice: messagingEnabled } = useReduxStore(state => state.settingsReducer.restaurantSettings.messaging_enabled);

    return useQuery(
        [CHAT_THREADS_KEY, UNREAD_THREADS_COUNT_KEY],
        () => getUnreadThreadsCount(),
        {
            ...options,
            enabled: Boolean(messagingEnabled),
            onError: () => {
                notifyError(t("queries:threads.error.get_unread_threads_count"));
            },
        }
    );
}

export const CHAT_THREADS_UNREAD_MESSAGES_COUNTS_KEY = "chat-threads-unread-messages-counts";

function normalizeThreadIds(threadIds) {
    const ids = Array.isArray(threadIds) ? threadIds : [threadIds];

    return [...new Set(ids)]
        .filter(threadId => threadId !== null && threadId !== undefined)
        .sort((firstId, secondId) => firstId - secondId);
}

function getThreadUnreadMessagesCountQueryKey(threadId) {
    return [
        CHAT_THREADS_KEY,
        threadId,
        CHAT_THREAD_UNREAD_MESSAGES_COUNT_KEY,
    ];
}

function getThreadsUnreadMessagesCountsQueryKey(threadIds) {
    return [
        CHAT_THREADS_KEY,
        CHAT_THREADS_UNREAD_MESSAGES_COUNTS_KEY,
        ...normalizeThreadIds(threadIds),
    ];
}

function getCachedUnreadMessagesCount(queryClient, threadId) {
    return queryClient.getQueryData(
        getThreadUnreadMessagesCountQueryKey(threadId)
    );
}

function setCachedUnreadMessagesCount(queryClient, threadId, data) {
    queryClient.setQueryData(
        getThreadUnreadMessagesCountQueryKey(threadId),
        data
    );
}

function setChatThreadsUnreadMessagesCountToZero(queryClient, threadIds) {
    const normalizedThreadIds = normalizeThreadIds(threadIds);

    normalizedThreadIds.forEach(threadId => {
        setCachedUnreadMessagesCount(queryClient, threadId, {
            id: threadId,
            unread_messages_count: 0,
        });
    });

    queryClient.invalidateQueries([CHAT_THREADS_KEY, CHAT_THREADS_UNREAD_MESSAGES_COUNTS_KEY]);
    queryClient.invalidateQueries([CHAT_THREADS_KEY, UNREAD_THREADS_COUNT_KEY]);
}

export function useGetChatThreadsUnreadMessagesCount(threadIds, options = {}) {
    const { t } = useTranslation();
    const { slice: messagingEnabled } = useReduxStore(
        state => state.settingsReducer.restaurantSettings.messaging_enabled
    );

    const normalizedThreadIds = normalizeThreadIds(threadIds);

    return useQuery(
        getThreadsUnreadMessagesCountsQueryKey(normalizedThreadIds),
        () => getChatThreadsUnreadMessagesCount(normalizedThreadIds),
        {
            ...options,
            enabled: Boolean(messagingEnabled) && normalizedThreadIds.length > 0,
            onError: () => {
                notifyError(t("queries:threads.error.get_chat_thread_unread_messages_count"));
            },
        }
    );
}


// WEBPACK FOOTER //
// ./src/features/messaging/queries/threads/hooks.js